import * as React from "react";
import { cn } from "../../lib/utils";
import { computeCalories } from "../../utils/caloriesUtil";

export interface MacroBarProps {
  fat: number;
  carbs: number;
  protein: number;
  kcal: number;
  className?: string;
  showLabels?: boolean;
  hideKcal?: boolean;
  showExtendedInfo?: boolean;
}

export const MacroBar = ({
  fat,
  carbs,
  protein,
  className,
  showLabels = true,
  hideKcal = false,
  showExtendedInfo = false,
  kcal,
}: MacroBarProps) => {
  const calorieSum = computeCalories({ carbs, fat, protein });

  const getPercent = (grams: number, multiplier: number) => {
    if (calorieSum === 0) return 0;
    // Don't round here to avoid 1px gaps (99% instead of 100%)
    return ((grams * multiplier) / calorieSum) * 100;
  };

  const fatPercent = getPercent(fat, 9);
  const carbsPercent = getPercent(carbs, 4);
  const proteinPercent = getPercent(protein, 4);

  return (
    <div className={cn("w-full", className)}>
      {showLabels && !showExtendedInfo && (
        <div className="mt-1 flex justify-between text-2xs tracking-wider text-ink-muted">
          <span>
            <span className="font-semibold text-gold">F</span> {Math.round(fat)}
            g • <span className="font-semibold text-teal">KH</span>{" "}
            {Math.round(carbs)}g •{" "}
            <span className="font-semibold text-protein">P</span>{" "}
            {Math.round(protein)}g
          </span>
          {!hideKcal && <span>{Math.round(kcal)} kcal</span>}
        </div>
      )}
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-line">
        <span
          className="block h-full bg-gold"
          style={{ width: `${fatPercent}%` }}
        />
        <span
          className="block h-full bg-teal"
          style={{ width: `${carbsPercent}%` }}
        />
        <span
          className="block h-full bg-protein"
          style={{ width: `${proteinPercent}%` }}
        />
      </div>
      {showLabels && showExtendedInfo && (
        <div className="mt-2 flex justify-between text-center text-xs tracking-wider">
          <div className="flex flex-col items-center">
            <span className="text-2xs font-semibold text-gold">Fett</span>
            <span className="mt-1 font-bold text-ink text-sm">
              {fat.toFixed(1)}g
            </span>
            <span className="text-2xs text-ink-muted">
              {Math.round(fatPercent)}%
            </span>
          </div>

          <div className="flex flex-col items-center">
            <span className="text-2xs font-semibold text-teal">KH</span>
            <span className="mt-1 font-bold text-ink text-sm">
              {Math.round(carbs)}g
            </span>
            <span className="text-2xs text-ink-muted">
              {Math.round(carbsPercent)}%
            </span>
          </div>

          <div className="flex flex-col items-center">
            <span className="text-2xs font-semibold text-protein">
              Protein
            </span>
            <span className="mt-1 font-bold text-ink text-sm">
              {protein.toFixed(1)}g
            </span>
            <span className="text-2xs text-ink-muted">
              {Math.round(proteinPercent)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
